// Card / pile lifecycle, extracted from GameBoard.jsx. Owns the
// movement of cards between zones: drawing from piles (to hand or
// straight into a drag), adding/removing table cards (mesh + physics
// + broadcast recipe), routing cards to piles (with the Site→Rubble
// rule and deathrite/equipment detach side effects), the
// findOrCreate* zone piles, drag-hover affordances over piles, and
// hand reveal/face-down toggles.
//
// Host contract: meshes, pileMeshes, physics, scene, state.gameState,
// sync/_broadcast* primitives, hand methods (addToHand/removeFromHand),
// effect cleanup (_disposeCardEffectsOnLeave, _fireDeathriteOnLeave),
// logAction. Zone-rule helpers (eligibleDestinations, pushDestination)
// stay in engine/automation.

import { UI } from '../../../utils/arena/uiSounds';
import { trackExitingMesh, untrackExitingMesh } from './boardReconciler';
import { animateCardToPile, animateShufflePile } from '../../../utils/game/animations';
import { CARD_HEIGHT, CARD_THICKNESS, CARD_WIDTH, buildStatusBadges, createCardMesh, createLifeHUD, createPileMesh, updatePileMesh } from '../../../utils/game/cardMesh';
import { TRIGGER, applyStatusToggle, getEffectiveStatuses, grantWardOnEntry, shouldGrantWardOnEntry } from '../../../utils/game/engine/automation';
import { countsAsMinion, getAuraShape, grantStealthOnEntry, reachableLocationsInSteps, rehydrateCardSets, resetCardOnLeavePlay, resolveAuraAnchor, tagCastLock } from '../../../utils/game/engine/core';
import { syncCardFace } from '../../../utils/game/engine/core/cardFace';
import { drawFromPile, shufflePile } from '../../../utils/game/gameState';
import { spellDrawCapActive, isSpellDrawBlocked } from '../../../utils/game/engine/automation/ops/spellDrawCap';
import { computeGridPositions } from '../../../utils/game/grid';
import { addCardBody, removeCardBody } from '../../../utils/game/physicsWorld';
import { playSound } from '../../../utils/game/sounds';
import { getGameGrid, getSpawnPoint } from '../../../utils/game/spawnConfig';
import { resolveLocalImageUrl } from '../../../utils/localApi';
import { DRAW_STAGGER_MS, isTopIndexedZone } from './authZones';
import { cardLogLabel } from './logbookChat';
import { msg } from '../../../utils/game/logMessage';
import { duplicateIntent } from '../../../utils/game/net/manualIntents';
import { isConnected, sendMatchCommand } from '../../../utils/game/socketClient';
import { toast } from 'sonner';
import { revealCommand } from './authReveals';
import { k, t } from '../../../localization/t';

// Drop zone around the local player's cemetery snap point. A card released
// inside this radius (in world units) registers in the local cemetery
// instead of falling back to the table. Roughly one card-height so the
// footprint matches the pile's visual footprint.
const CEMETERY_DROP_RADIUS = CARD_HEIGHT;
const CEMETERY_DROP_RADIUS_SQ = CEMETERY_DROP_RADIUS * CEMETERY_DROP_RADIUS;

// Sync a pile mutation to the opponent. Auth mode (R2c): a card moving to a
// named zone is an authoritative MOVE_TO_ZONE. The server records the move and
// reveals it per-seat (a public cemetery/banished card to both; a card returned
// to the hidden spellbook/atlas/collection only to its owner, opponent sees the
// count). The leaky legacy pile:sync (which broadcasts the FULL pile contents) is
// skipped for HIDDEN destination piles so it can't re-leak the deck order; public
// piles (cemetery/banished) keep pile:sync until their opponent-side rendering
// migrates to the projection (R2d). Legacy / sandbox always use pile:sync.
function emitPileSyncOrZone(host, pile, pileName, cardInstance, position) {
  const authZone = host._authZonesEnabled?.() ? host._authZoneForPileName(pileName) : null;
  if (authZone) {
    // Same boundary as authZones.renderOrderFromMirror, in the outgoing
    // direction: `position` names the RENDER end the card was just inserted at
    // (see the pile.unshift/push below), and mirror index 0 is the opposite end
    // for a top-indexed draw pile. Sending a card to the deck bottom while
    // telling the server index 0 buries it at the TOP, and the next draw hands
    // it straight back. GH-76 through the "Send to → Bottom" menu.
    const rendersOnTop = position !== 'bottom';
    const toMirrorHead = isTopIndexedZone(authZone) ? rendersOnTop : !rendersOnTop;
    const opts = toMirrorHead ? { index: 0 } : undefined;
    host._emitMoveToZone(cardInstance.id, authZone, opts);
    // Hidden destination → the count rides the authoritative diff; suppress the
    // leaky full-contents pile:sync. Public discard piles still sync for now.
    if (authZone === 'spellbook' || authZone === 'atlas' || authZone === 'collection') return;
  }
}

// --- Game Actions ---
// Draw choreography: a translucent ghost of the drawn card flies from a source
// mesh (the drawer's Spellbook/Atlas pile) to a destination. The player hand
// slot by default, or a unit's mesh (`destMesh`) for owner-locked unit-hand
// draws. Purely cosmetic. The card's real hand entry is owned by state. No-op
// in a headless/host-stub context (no camera/canvas) or when the source mesh is
// missing. Shared by the manual draw (drawCard) and the server-autonomous
// hand-additions fold so both look identical.
export function flyCardToHand(host, card, sourceMesh, { destMesh = null } = {}) {
  if (!sourceMesh || !host.scene?.camera || !host.canvasRef?.current) return;
  const canvas = host.canvasRef.current;
  const toScreen = (mesh) => {
    const p = mesh.position.clone();
    p.y += 2;
    p.project(host.scene.camera);
    return { x: ((p.x + 1) / 2) * canvas.clientWidth, y: ((-p.y + 1) / 2) * canvas.clientHeight };
  };
  const start = toScreen(sourceMesh);

  const flyCard = document.createElement('div');
  flyCard.style.cssText = `
    position: fixed; z-index: 2000; pointer-events: none;
    width: 5cqw; height: 7cqw; border-radius: var(--radius-lg); overflow: hidden;
    left: ${start.x}px; top: ${start.y}px;
    transform: translate(-50%, -50%) scale(0.5);
    transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    box-shadow: 0 8px 30px rgba(0,0,0,0.5);
  `;
  const img = document.createElement('img');
  img.src = resolveLocalImageUrl(card.imageUrl);
  img.style.cssText = 'width: 100%; height: 100%; object-fit: cover;';
  img.draggable = false;
  flyCard.appendChild(img);
  document.body.appendChild(flyCard);

  // Owner-locked unit-hand draws land on the unit's badge, not the player fan.
  const dest = destMesh ? toScreen(destMesh) : { x: window.innerWidth / 2, y: window.innerHeight - 40 };
  requestAnimationFrame(() => {
    flyCard.style.left = `${dest.x}px`;
    flyCard.style.top = `${dest.y}px`;
    flyCard.style.transform = `translate(-50%, -50%) scale(${destMesh ? 0.4 : 1})`;
    flyCard.style.opacity = '0.6';
  });

  setTimeout(() => {
    flyCard.remove();
  }, 450);
}

// The drawer's Spellbook pile mesh. The fly-ghost origin for a card that
// entered `seat`'s hand. Null when the pile/mesh isn't rendered yet.
function spellbookMeshFor(host, seat) {
  const pile = host.state?.gameState?.piles?.find((p) => p.name === 'Spellbook' && !!p.rotated === (seat === 'p2'));
  return pile ? host.pileMeshes.get(pile.id) : null;
}

// Server-autonomous draw landing in the main hand: play the draw sound + fly a
// ghost from the drawer's Spellbook to the hand. Called per card by the
// staggered scheduler in authZones.
export function animateDrawnCardToHand(host, card, seat) {
  playSound('cardDraw');
  flyCardToHand(host, card, spellbookMeshFor(host, seat));
}

// Owner-locked (unit-hand) server-autonomous draws: fired from the unitHands
// fold. Diffs each locally-controlled unit's spell list against the current
// state and, for every newly-added spell, plays a staggered draw sound + a ghost
// flying from the drawer's Spellbook onto the unit's badge. The badge count
// itself is still folded instantly (server truth). This only adds choreography.
export function animateUnitHandAdditions(host, nextUnitHands) {
  if (!nextUnitHands || !host.scene?.camera || !host.canvasRef?.current) return;
  const prev = host.state?.gameState?.unitHands || {};
  const localSeat = host._localSeat?.();
  for (const [ownerId, entry] of Object.entries(nextUnitHands)) {
    const controller = entry?.controller;
    // Only OUR draws (sandbox pilots both seats). Opponent unit-hand spells
    // arrive as identity-less backs anyway. Nothing to fly.
    if (!host.props?.isCasualSandbox && controller && controller !== localSeat) continue;
    const prevIds = new Set((prev[ownerId]?.spells || []).map((s) => s.id));
    const fresh = (entry?.spells || []).filter((s) => s?.id && !prevIds.has(s.id));
    if (fresh.length === 0) continue;
    const unitMesh = host.meshes.get(ownerId);
    if (!unitMesh) continue;
    const sourceMesh = spellbookMeshFor(host, controller === 'p2' ? 'p2' : 'p1');
    fresh.forEach((spell, i) => {
      setTimeout(() => {
        if (host._unmounted) return;
        playSound('cardDraw');
        flyCardToHand(host, spell, sourceMesh, { destMesh: unitMesh });
      }, i * DRAW_STAGGER_MS);
    });
  }
}

export function drawCard(host, pileId, { skipLog = false, skipSync = false, from = 'top', castLockOwnerId = null, castLockOwnerArt = null } = {}) {
  if (host.props.isSpectating) return null;
  const { gameState } = host.state;
  const pileMesh = host.pileMeshes.get(pileId);
  const pile = gameState.piles.find((p) => p.id === pileId);

  // Garden of Eden: "Players can't draw more than one spell each turn."
  // A Spellbook draw is a spell draw; block the 2nd+ this turn while any
  // cap source is in play. Atlas (site) draws are never affected. This is
  // the UX choke. Under auth the SERVER enforces the same cap (its
  // spellDrawsThisTurn counter + draw-site clamps), so read the folded
  // counter too: the local one covers the optimistic round-trip window,
  // the mirror one covers server-side draws + a reload.
  if (pile?.name === 'Spellbook') {
    const drawer = pile.rotated ? 'p2' : 'p1';
    const already = Math.max(
      host._spellDrawsThisTurn?.[drawer] || 0,
      host._authZonesEnabled?.() ? (host._authMirror?.getState?.()?.spellDrawsThisTurn?.[drawer] || 0) : 0,
    );
    if (isSpellDrawBlocked(already, spellDrawCapActive(host.meshes, host._getFullCard))) {
      host.logAction("Can't draw more than one spell this turn (Garden of Eden)");
      return null;
    }
  }

  const card = drawFromPile(gameState, pileId, from);
  if (!card) return null;
  // Normalize on the way OUT of the pile, the symmetric counterpart of
  // resetCardOnLeavePlay. The leave-play reset only runs on the acting
  // client; a client that adopted this pile via pile:sync / state
  // reconciliation may still hold a non-reset copy (e.g. an Enchantress-
  // animated aura that died as a damage-marked Minion). Without this the
  // card re-enters the realm trapped. AddCardToTable only re-inits stats
  // when they're undefined, so the death sweep instantly re-kills it.
  resetCardOnLeavePlay(card);
  // Owner-locked draw (Omphalos / Morgana). Tag AFTER the reset so the
  // lock isn't wiped. The tag rides natively on the instance when the
  // card is later played face-up; also broadcast it so the peer's copy
  // carries the same lock through any state reconciliation.
  if (castLockOwnerId) {
    tagCastLock(card, castLockOwnerId);
    // Auth: the lock rides SET_UNIT_HANDS. Never the legacy relay.
    if (!skipSync && !host._authZonesEnabled?.()) host.sync?.castLock(card.id, castLockOwnerId);
  }
  playSound('cardDraw');
  if (!skipLog) {
    host.logAction('Drew a card from {pile}', { pile: msg(pile?.name || k('pile')) });
  }

  // Broadcast the pile shrink to the opponent. Auth mode (R2c): a draw is an
  // authoritative MOVE_TO_ZONE of the drawn card into the drawer's hand. The
  // server reveals the card to the drawer's projection and propagates only the
  // COUNT delta to the opponent. The legacy pile:sync would re-leak the hidden
  // draw-pile contents, so it's skipped for the deck/collection piles in auth
  // mode (the count now rides the authoritative diff). Public piles (cemetery/
  // banished) + legacy/sandbox keep pile:sync byte-unchanged.
  const authZone = (pile && host._authZonesEnabled?.()) ? host._authZoneForPileName(pile.name) : null;
  if (authZone === 'spellbook' || authZone === 'atlas' || authZone === 'collection') {
    // The authoritative per-card move MUST fire for EVERY drawn card, even in a
    // multi-draw (hotkey N) where skipSync batches the LEGACY whole-pile sync.
    // Skipping it left the earlier cards in the server's pile. Never moved to
    // hand. So they vanished on the next hand reconcile. skipSync only
    // suppresses the legacy pile:sync + log, never the authoritative move.
    host._emitMoveToZone(card.id, 'hand');
  } else if (!skipSync && pile) {
  }

  // Animate card flying from pile to hand
  flyCardToHand(host, card, pileMesh);

  // Deposit into the RECIPIENT seat's hand (the pile owner), relaying
  // cross-seat. Owner-locked draws go to the drawing unit's own hand.
  depositDrawnCard(host, card, pile, { castLockOwnerId, castLockOwnerArt });
  host.setState((state) => ({ gameState: { ...state.gameState } }));
  host.updatePileMeshes();

  // Single draw choke point: a card leaving a deck (Spellbook / Atlas)
  // for a hand is a Sorcery "draw." Increment the per-turn counter and
  // fire on_draw once per card. Cemetery / collection retrieval is a
  // manual move, not a draw, so it's excluded. Pile ownership (rotated)
  // identifies the drawing player. fireOnDraw self-guards re-entrancy,
  // so a nested draw made while resolving on_draw still counts here but
  // doesn't re-fire the trigger (Queen of Midland loop safety).
  if (pile?.name === 'Spellbook' || pile?.name === 'Atlas') {
    const drawer = pile.rotated ? 'p2' : 'p1';
    // Legacy-path bookkeeping only: under auth the SERVER's
    // state.cardsDrawnThisTurn is authoritative (drawTriggers fan-out) and
    // Iron Maiden's counter read never runs locally (serverOwnsOnDrawEntry).
    if (!host._cardsDrawnThisTurn) host._cardsDrawnThisTurn = { p1: 0, p2: 0 };
    host._cardsDrawnThisTurn[drawer] += 1;
    // Spell-only counter for Garden of Eden's cap. Spellbook draws are
    // spells; Atlas draws are sites and never count.
    if (pile.name === 'Spellbook') {
      if (!host._spellDrawsThisTurn) host._spellDrawsThisTurn = { p1: 0, p2: 0 };
      host._spellDrawsThisTurn[drawer] += 1;
    }
    host._fireOnDraw?.(drawer);
  }

  return card;
}

// Deposit a freshly drawn card into the RECIPIENT seat's hand. The
// recipient is the pile's owner (pile.rotated → 'p2', else 'p1'). drawCard
// runs on the client that RESOLVES the effect, which is not always the
// recipient: False Idol's "its opponent draws", Seelie Court's both-seats
// fan, and Queen of Midland's bonus draw all draw for a player who may not
// be the local seat. addToHand routes to the LOCAL hand in multiplayer, so
// a naive deposit would drop the opponent's drawn card into the acting
// player's hand (their spellbook shrink folds from the authoritative diff, but the
// card materialises on the wrong side). Mirror returnCardToOwnerHand: when
// the recipient is local (or casual sandbox, one client both seats) deposit
// here; otherwise relay the card to the recipient's client, which adds it to
// THEIR hand. The pile shrink is already broadcast by drawCard.
export function depositDrawnCard(host, card, pile, { castLockOwnerId = null, castLockOwnerArt = null } = {}) {
  const recipientSeat = pile?.rotated ? 'p2' : 'p1';
  const recipientIsLocal = host.props.isCasualSandbox || recipientSeat === host._localSeat();
  if (recipientIsLocal) {
    if (castLockOwnerId) host.addToUnitHand(castLockOwnerId, card, castLockOwnerArt);
    else host.addToHand(card);
    return;
  }
  // Auth: server-owned draws land in the recipient's hand via the fold.
  if (host._authZonesEnabled?.()) return;
  host.sync?.drawToHand?.(card, recipientSeat, { castLockOwnerId, castLockOwnerArt });
}

// A player's own click / menu draw. Under auth a deck draw is one DRAW command
// and the card lands from the hand-additions fold (no optimistic hand write to
// revert); returns true once the DRAW is sent, null when a choke declined it.
// Everything else keeps the local drawCard path.
export function playerDrawFromPile(host, pileId) {
  if (host.props.isSpectating) return null;
  const pile = host.state.gameState?.piles?.find((p) => p.id === pileId);
  const authZone = pile && host._authZonesEnabled?.() ? host._authZoneForPileName?.(pile.name) : null;
  if (authZone !== 'spellbook' && authZone !== 'atlas') return host.drawCard(pileId);
  if (!isConnected()) { toast(t('Reconnecting. Try again in a moment.')); return null; }
  if (pile.cards.length === 0) return null;
  const seat = pile.rotated ? 'p2' : 'p1';
  // Garden of Eden: the server rejects a capped Spellbook draw silently, so
  // choke it here with the same test drawCard applies.
  if (authZone === 'spellbook') {
    const already = host._authMirror?.getState?.()?.spellDrawsThisTurn?.[seat] || 0;
    if (isSpellDrawBlocked(already, spellDrawCapActive(host.meshes, host._getFullCard))) {
      host.logAction("Can't draw more than one spell this turn (Garden of Eden)");
      return null;
    }
  }
  host.logAction('Drew a card from {pile}', { pile: msg(pile.name) });
  host._emitDraw(seat, authZone, 1);
  return true;
}

export function drawCardToDrag(host, pileId, pileMeshHit) {
  const { gameState } = host.state;
  const pile = gameState.piles.find((p) => p.id === pileId);
  // Capture the pile's top BEFORE drawFromPile shrinks it. The pile
  // mesh centers at thickness/2, so the top surface sits at 2× its
  // y position. Spawning the dragged card just above this makes it
  // look pulled off the top of the deck rather than the middle.
  const pileTopY = pileMeshHit.position.y * 2;
  const card = drawFromPile(gameState, pileId);
  if (!card) return;
  // Symmetric counterpart of resetCardOnLeavePlay (see drawCard). Strip
  // any transient in-realm state the pile copy may still carry so the
  // card re-enters as a fresh spawn. Reverts a still-animated Enchantress
  // aura back to its printed form instead of replaying a damage-marked
  // Minion that the death sweep instantly re-kills.
  resetCardOnLeavePlay(card);

  // Dragging from a pile is still a draw action. Log it the same
  // way a click-to-hand does. The opponent's pile:sync handler logs
  // "Drew 1 card from <pile>" from their side.
  host.logAction('Drew a card from {pile}', { pile: msg(pile?.name || k('pile')) });

  // Sync the shrunk pile to the opponent

  // Stage the card on the table for a drag. Play-commit effects
  // (log, mana, affinity) fire later in handleMouseUp when the
  // drag actually ends on the table. Drag-from-pile draws always
  // spawn face-down. Some mechanics require the card to be hidden
  // until the player explicitly flips it on the board.
  card.faceDown = true;
  syncCardFace(card);
  card.x = pileMeshHit.position.x;
  card.z = pileMeshHit.position.z;
  host.addCardToTable(card, true, { staged: true });
  host.updatePileMeshes();

  // Immediately start dragging the newly spawned card
  const mesh = host.meshes.get(card.id);
  if (mesh) {
    // Place the card right on top of the deck pile so the drag
    // visually begins at the surface the user clicked. startCardDrag
    // reads mesh.position.y as initialY and syncs the kinematic
    // body, so overriding here is sufficient.
    mesh.position.y = pileTopY + CARD_THICKNESS / 2;
    host.startCardDrag({
      primaryMesh: mesh,
      dragIds: new Set([card.id]),
      anchorX: mesh.position.x,
      anchorZ: mesh.position.z,
      freshPlay: true,
    });
  }

  playSound('cardDraw');
  host.setState((state) => ({ gameState: { ...state.gameState } }));
}

export function playCardFromHand(host, cardInstance, event) {
  const point = host.scene.raycastTablePoint(event);
  if (!point) return;

  cardInstance.x = point.x;
  cardInstance.z = point.z;

  host.removeFromHand(cardInstance);
  host.addCardToTable(cardInstance);
  playSound('cardPlace');
  // Auth: record the hand → board move so matchState.hands drops the played
  // card. Without it the mirror's hand stays stale and a later rebuild
  // (reconnect / gap-heal Full) would restore the played card as a hand copy.
  if (host._authZonesEnabled?.()) host._emitMoveToZone(cardInstance.id, 'play');
  // Force a server snapshot now so a quick disconnect can't lose the just-played
  // card on reconnect (the legacy board blob still owns positions; see
  // finalizeFreshPlayCommit).
  host.autoSave?.({ force: true })?.catch?.(() => {});
}

/**
 * Re-derive the catalog-owned fields onto a card instance entering the
 * table. Exported for the manual-session regression tests; every table
 * card funnels through addCardToTable, which is its only caller.
 */
export function hydrateCatalogFields(host, cardInstance) {
  // Hydrate printed keywords (v1.15a) so the scope resolver's status
  // filter recognises baseline-printed keywords like Spellcaster on
  // Apprentice Wizard. Printed keywords are catalog data. They
  // never mutate at runtime. So we ALWAYS re-derive from the
  // catalog rather than trusting whatever was already on the
  // instance. The previous "only-if-undefined" guard kept stale
  // empties around forever when the extractor was later improved
  // (v1.34. Phantom Steed's "Movement +2, Voidwalk" line was
  // historically rejected; once the extractor learned to handle
  // parameterised keywords, existing card instances kept their
  // empty array and Voidwalk never lit up).
  // qa#121: a manual (automations-off) session hydrates NO printed keywords.
  // The set is the client's automation input, not card text: it is what lights
  // the status badges, what `shouldGrantWardOnEntry` / `shouldGrantStealthOnEntry`
  // / `grantLanceOnEntry` read to place their entry tokens, and what movement
  // and combat read for airborne / voidwalk / ranged. Manual mode tracks
  // elemental affinity, mana and HP and nothing else, so every keyword on a
  // manual card is one a player placed by hand through the status ring. The
  // card's own rules text is unaffected. The inspector reads the catalog row.
  const fullCard = host._getFullCard(cardInstance.cardId);
  cardInstance._printedKeywords = host._automationsEnabled?.() === false || !fullCard?.printedKeywords
    ? []
    : [...fullCard.printedKeywords];
  // v1.52. Movement +N bonus from the printed rules text. Used
  // by reachableLocationsInSteps to compute the step budget
  // (1 + bonus). 0 for units without the keyword. Re-derived
  // from the catalog on every addCardToTable so session-restored
  // cards pick up the field automatically.
  cardInstance._movementBonus = fullCard?.movementBonus || 0;
  // v1.56. Ranged N value. Drives combat target envelope
  // (Chebyshev radius) and projectile-attack rule (no defender
  // strike-back from outside melee range). 0 for non-Ranged units.
  cardInstance._rangedValue = fullCard?.rangedValue || 0;
  // Aura placement shape. INTERSECTION_4 by default, with per-card
  // overrides for walls (INTERSECTION_2) and atop-site auras. The
  // shape descriptor drives both drag-time halo rendering and the
  // drop-commit snap math. Re-derived on every addCardToTable so
  // session-restored aura cards inherit the right shape automatically.
  //
  // `_auraAnchor` is the grid-coord form of the aura's current
  // position. `{ col, row }` for INTERSECTION_4 / ATOP_SITE,
  // `{ col, row, edge }` for INTERSECTION_2 walls. It's reverse-
  // resolved from card.x/z so session-restored cards rebuild their
  // anchor without saving the anchor explicitly. Scope resolution
  // for the 'covered' scope reads this directly.
  if (fullCard?.type === 'Aura' || cardInstance.type === 'Aura') {
    cardInstance._auraShape = getAuraShape(fullCard || cardInstance);
    const grid = getGameGrid(host.state.spawnConfig);
    if (grid) {
      const { colPositions, rowPositions } = computeGridPositions(grid);
      const anchor = resolveAuraAnchor(
        cardInstance, grid, colPositions.length - 1, rowPositions.length - 1, host._cellAtWorld,
      );
      if (anchor) cardInstance._auraAnchor = anchor;
    }
  }
}

export function addCardToTable(host, cardInstance, broadcast = true, { staged = false, silent = false, logVerb = null } = {}) {
  // Idempotency guard: refuse to add a second mesh for a card that's
  // already on the table. Without this, a double-fire (rapid context-
  // menu click, retransmitted card:place, racy spawn-from-pile) would
  // orphan the existing mesh. Left in the scene + physics world but
  // no longer in host.meshes, so raycasting can never hit it again
  // and the duplicate becomes permanently un-interactable.
  if (host.meshes.has(cardInstance.id)) {
    console.warn(`[addCardToTable] skipping duplicate mesh for ${cardInstance.id} (${cardInstance.name || 'unknown'})`);
    return;
  }
  // Re-hydrate a card we already know if it arrived stripped (a
  // sanitized face-down copy bouncing back through a pile/hand), then
  // record its identity for future syncs. remember() ignores blanks,
  // so an opponent's genuinely-hidden card never gets cached here.
  host.identityVault.restore(cardInstance);
  host.identityVault.remember(cardInstance);
  // A card placed face up on the board is visible to the opponent, so its
  // identity is now public and the logbook may name it from here on (the
  // flag is sticky. It survives the card later flipping face down or
  // bouncing back to a hidden zone). This is the single adoption point for
  // every table card on BOTH clients (local play AND received card:place),
  // so it covers the common case without per-call-site bookkeeping. A
  // face-down placement leaves it unset → cardLogLabel anonymizes it.
  if (!cardInstance.faceDown) cardInstance.nameKnown = true;
  // Normalize Set-typed bookkeeping fields (_abilitiesUsedThisTurn et
  // al.) that arrive as arrays from a save blob. Or as the corrupt
  // `{}` a raw JSON round-trip leaves behind. So every downstream
  // reader (.has/.clear in the turn-phase runner, automation gates,
  // ready-glow sync) can rely on them being real Sets. Central here
  // because this is the single adoption point for ALL table cards:
  // saves, P2P place, auth snapshots, pile spawns.
  rehydrateCardSets(cardInstance);
  // Assign an interaction order if the card doesn't already have one.
  // Cards loaded from a saved match keep their stored order so the
  // restored stack reads top-to-bottom in the same way it was saved.
  // Cards spawned fresh (drag from hand, deck draw, opponent's place
  // received and forwarded the order) just need a fresh number; the
  // counter tracks the max, so a fresh card always lands on top of
  // existing ones.
  if (typeof cardInstance.interactionOrder !== 'number') {
    cardInstance.interactionOrder = host._nextInteractionOrder();
  } else if (cardInstance.interactionOrder > host._interactionCounter) {
    host._interactionCounter = cardInstance.interactionOrder;
  }
  // v1.58. Stamp the realm-entry timestamp for the layer system.
  // Restored sessions carry their saved timestamp; fresh plays get a
  // new monotonic value. The recompute uses this to order ongoing
  // effects by timestamp asc within each layer (older first).
  if (typeof cardInstance._enteredRealmAt !== 'number') {
    cardInstance._enteredRealmAt = host._layerTimestampGen();
  }
  hydrateCatalogFields(host, cardInstance);
  // Imposter latch: stamp `_isImposter` the first time we see this
  // card so the Mask ability survives an in-match identity swap. The
  // moment masking mutates `cardId`, the registry lookup would point
  // at the masked avatar's rules and the Mask entry would vanish from
  // the action menu. This flag lets `_activatedEntriesFor` keep the
  // entry visible regardless of the current cardId.
  if (
    !cardInstance._isImposter
    && cardInstance.type === 'Avatar'
    && cardInstance.cardId === 'sorcery-imposter'
  ) {
    cardInstance._isImposter = true;
  }
  // v1.19. Backfill subTypes for cards that pre-date the
  // createCardInstance fix (saved sessions, networked card payloads
  // from older clients). Without this, scope.filter.subtype checks
  // silently miss for in-flight cards. Cheap one-time lookup.
  if (!cardInstance.subTypes || cardInstance.subTypes.length === 0) {
    const fullCard = host._getFullCard(cardInstance.cardId);
    if (Array.isArray(fullCard?.traits) && fullCard.traits.length > 0) {
      cardInstance.subTypes = [...fullCard.traits];
    } else if (!cardInstance.subTypes) {
      cardInstance.subTypes = [];
    }
  }
  // Animist's "cast a magic as a Spirit" flip used to live here, at the
  // staging boundary. But staging is provisional (a card bounced back to
  // hand mid-drag is reset), so the flip now happens at the authoritative
  // play-commit (cardStateOps.finalizeFreshPlayCommit), gated on the armed
  // flag there.
  delete cardInstance._fromHand;
  // Auto-grant the Ward Token on first entry (Sorcery rule). Pure
  // mutation lives in wards.js. `shouldGrantWardOnEntry` checks
  // the printed keyword + already-granted flag, `grantWardOnEntry`
  // mutates statuses + sets _wardGranted.
  if (shouldGrantWardOnEntry(cardInstance)) grantWardOnEntry(cardInstance);
  // (v1.107 snap moved to the fresh-play commit in handleMouseUp.
  // see _snapSiteToOwnerEdge call there. Hand→table plays go
  // staged-drag, so by the time addCardToTable runs the card is
  // still at the hand/pile spawn pose and _cellAtWorld is null;
  // doing the snap after the drag ended is the only point where
  // mesh.position carries the actual drop coords.)
  // v1.55. Stealth token on realm entry for minions with the
  // Stealth keyword (codex: "A minion with the Stealth keyword
  // enters the realm with a Stealth token"). Idempotent. A bounce
  // + replay refreshes the token. Pure mutation lives in stealth.js.
  grantStealthOnEntry(cardInstance);
  const mesh = createCardMesh(cardInstance);
  host.scene.scene.add(mesh);
  host.meshes.set(cardInstance.id, mesh);
  host.applyOwnershipOutline(mesh);

  // createCardMesh now initializes mesh.userData.intentEuler with the
  // full target orientation (faceDown + tapped + isSite + rotated),
  // so the mesh renders in the correct pose on its first frame. No
  // separate post-spawn rotation pass is needed.

  // Add ATK + HP HUD for minion-type cards (countsAsMinion. Printed
  // Minions plus Automatons, which are artifacts that are minions per
  // codex, e.g. Kairos the Archivist). Spells (Magic), Auras, plain
  // Artifacts, and Sites have no combat stats. Avatars get an
  // attack-only pill. Their power (1, 2, or 3 depending on the
  // avatar) can be modified via the shift-click status ring, but
  // defense is irrelevant since avatar HP lives on the player
  // life tracker, not on the card itself.
  if (countsAsMinion(cardInstance)) {
    if (
      cardInstance.currentLife === undefined
      || cardInstance.currentAttack === undefined
      || cardInstance.baseAttack === undefined
      || cardInstance.baseLife === undefined
    ) {
      const fullCard = host._getFullCard(cardInstance.cardId);
      const defense = parseInt(fullCard?.defense, 10);
      const power = parseInt(fullCard?.power, 10);
      const baseAtk = power > 0 ? power : 0;
      const baseDef = defense > 0 ? defense : 0;
      // Cached on the instance so the hover overlay can compute
      // ATK / DEF deltas without re-walking the card catalog. Set
      // alongside the current values (and back-filled here for
      // session-restored cards that pre-date this field).
      if (cardInstance.baseAttack === undefined) cardInstance.baseAttack = baseAtk;
      if (cardInstance.baseLife === undefined) cardInstance.baseLife = baseDef;
      if (cardInstance.currentLife === undefined) cardInstance.currentLife = baseDef;
      if (cardInstance.currentAttack === undefined) cardInstance.currentAttack = baseAtk;
    }
    const hud = createLifeHUD(cardInstance, host.state.isHost);
    mesh.add(hud.sprite);      // ATK display
    mesh.add(hud.hpSprite);    // HP display
    if (hud.movSprite) mesh.add(hud.movSprite); // Movement +/- modifier badge
    host.lifeHUDs.set(cardInstance.id, hud);
  } else if (cardInstance.type === 'Avatar') {
    if (cardInstance.currentAttack === undefined || cardInstance.baseAttack === undefined) {
      const fullCard = host._getFullCard(cardInstance.cardId);
      const power = parseInt(fullCard?.power, 10);
      const baseAtk = power > 0 ? power : 0;
      if (cardInstance.baseAttack === undefined) cardInstance.baseAttack = baseAtk;
      if (cardInstance.currentAttack === undefined) cardInstance.currentAttack = baseAtk;
    }
    const hud = createLifeHUD(cardInstance, host.state.isHost, { atkOnly: true });
    mesh.add(hud.sprite);
    host.lifeHUDs.set(cardInstance.id, hud);
  }

  // Build the initial status badges (printed keywords like
  // Airborne on Ancient Dragon, plus any manually-toggled or
  // restored-from-session statuses, plus any aura grants). Routing
  // through getEffectiveStatuses unifies all three sources. Using
  // raw cardInstance.statuses here would miss printed keywords and
  // leave Ancient Dragon's Airborne badge invisible until something
  // forced a rebuild.
  const effective = getEffectiveStatuses(cardInstance);
  if (effective.length > 0) {
    const badges = buildStatusBadges(effective, cardInstance.isSite);
    for (const badge of badges) mesh.add(badge);
    host.statusHUDs.set(cardInstance.id, badges);
    // Re-instantiate any 3D effects that go alongside these badges
    // (stealth smoke, ward shield, airborne wind, …).
    host._syncCardEffects(cardInstance.id, effective);
  }

  // Centred level-counter token for cards that track counters (The
  // Immortal Throne). Shown from level 0 so a fresh card reads "0".
  host._refreshCounterHUD(cardInstance.id);

  // Lance token on realm entry for units with the Lance keyword (codex:
  // "A minion with the Lance keyword enters the realm carrying a Lance
  // artifact token"). Mirrors the Stealth/Ward grants above, but Lance is
  // equipment, not a status, so it must run after the mesh exists and the
  // badges + equipment overlay need a rebuild. Populating equippedCards
  // here (before the card:place broadcast below) carries the Lance to the
  // opponent in the place payload. No separate equipment broadcast.
  // Actor-only: a relayed / mirror-rebuilt placement (broadcast=false) must
  // NEVER mint its own Lance. The actor's mint rides the place payload's
  // equippedCards (and the server's equipment state on a rebuild), so a
  // receive-side mint created a second Lance with a DIVERGENT id per client.
  // Auth: the SERVER grants the Lance at the cast commit (castService). The
  // cast intent never carried equippedCards, so a local mint here was
  // invisible to the server (no combat bonus, no break) AND doubled with the
  // folded server copy. The mirror fold renders the server's token.
  if (broadcast && !host._authZonesEnabled?.() && host._grantLanceOnEntry(cardInstance)) {
    host.rebuildStatusBadges(cardInstance.id);
    host.syncEquipmentOverlays();
  }

  // Restore saved Y position (for stacked cards) or use default
  if (cardInstance.y !== undefined && cardInstance.y > 0) {
    mesh.position.y = cardInstance.y;
  }

  // Spawn ABOVE any existing stack at the target position so the
  // newly placed card always lands on top. Never inside or below
  // an existing stack. The drop is what gives the "tossed onto the
  // table" feel; the dynamic height ensures it works for empty
  // patches and tall stacks alike.
  if (!host.sync.isSuppressed) {
    const stackTop = host.findStackHeightAt(mesh.position.x, mesh.position.z, cardInstance.id);
    mesh.position.y = stackTop + CARD_THICKNESS + 5;
  }

  // Create a matching dynamic body so cards collide and stack naturally.
  // `landscape` mirrors the visual intent. Sites sit rotated -90° by
  // default, and tapped non-sites are rotated -90° by computeTargetZ.
  // Keeping the collider aligned with the visual footprint is what
  // stops dropped cards from passing through the long edges of a
  // tapped or site card.
  addCardBody(host.physicsWorld, mesh, {
    width: CARD_WIDTH,
    height: CARD_HEIGHT,
    thickness: CARD_THICKNESS,
    landscape: Boolean(cardInstance.isSite) !== Boolean(cardInstance.tapped),
  });

  if (broadcast) {
    // A staged Magic is a LOCAL-ONLY drag visual. The card leaves the
    // square for the storyline the moment the cast commits, and only lands
    // on the board once it has resolved (castMagicFromHand →
    // landCastSpellOnBoard, which broadcasts the real placement).
    // Broadcasting the staged pose would flash a board card on the
    // opponent's screen mid-cast. (Animist's cast-as-Spirit magics are
    // already transformed to Minions here, so they still broadcast and
    // enter the realm correctly.)
    const stagedMagic = staged && cardInstance.type === 'Magic';
    if (!stagedMagic) host._broadcastPlaceCard(cardInstance, { staged, silent, logVerb });
    // A staged placement (the card is only on the table because the
    // user is dragging it) defers every play-commit effect. The
    // "Played" log, the site mana bump, and the elemental affinity
    // recalc. Until the drag actually ends on the table. See
    // handleMouseUp for where those effects fire. `silent` skips
    // the local play-log only. Used when the caller is going to
    // write a more accurate log line itself (e.g. "X dropped Y" on
    // equipment auto-detach). `logVerb` overrides the default
    // "Played" prefix (e.g. "Spawned" for status tokens dropped
    // from the StatusTokenDrawer). Same verb is forwarded to the
    // opponent's logbook so both sides read identically.
    if (!staged && !silent) {
      const label = cardInstance.faceDown ? msg('a card face down') : cardLogLabel(cardInstance);
      const verb = msg(logVerb || k('Played'));
      const cellNumber = host._cellNumberAt(cardInstance.x ?? 0, cardInstance.z ?? 0);
      if (cellNumber != null) host.logAction('{verb} {card} on [{cell}]', { verb, card: label, cell: cellNumber });
      else host.logAction('{verb} {card}', { verb, card: label });
      // Site play-effects (mana bump, affinity recalc) reveal elemental
      // threshold. Keep them hidden while the card is face-down.
      if (!cardInstance.faceDown) {
        host.applySitePlayEffects(cardInstance);
        // v1.42. Fire ON_REALM_ENTRY watchers after self-Genesis.
        host._fireRealmTrigger(TRIGGER.ON_REALM_ENTRY, cardInstance);
      }
    }
    // Positions domain (D5): a committed local placement (programmatic spawn.
    // site, token, summon, a resolved spell landing) asserts its board location
    // to the server. Staged drags defer to _presenceRelease at drop;
    // receives/restores (broadcast=false) never re-emit.
    if (!staged) {
      host._emitPosition?.(cardInstance.id, mesh.position.x, mesh.position.z);
    }
  }
  if (cardInstance.equippedCards?.length) {
    host.syncEquipmentOverlays();
  }
}

export function removeCardFromTable(host, cardInstance, broadcast = true, { staged = false, detachEquipmentToField = true } = {}) {
  const mesh = host.meshes.get(cardInstance.id);
  // Snapshot the host's position BEFORE the mesh is destroyed so any
  // attached equipment can be re-spawned exactly where it was last
  // worn. Skipped for the equip path itself (the card is leaving the
  // table specifically to be attached, not because the host moved off
  // the field) and for staged-play undo (cards in that state have no
  // equipment by construction).
  if (mesh && detachEquipmentToField && !staged) {
    const anchor = { x: mesh.position.x, z: mesh.position.z };
    host._detachAllEquipmentToField(cardInstance, anchor, { broadcast });
  }
  if (mesh) {
    // Strip from any lock-constraint chain before the body is
    // destroyed so the chain is rebuilt around the survivors.
    host.evictCardFromGroup(cardInstance.id);
    // Tear down physics body before disposing the mesh.
    removeCardBody(host.physicsWorld, mesh);
    host.scene.scene.remove(mesh);
    mesh.geometry.dispose();
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    mats.forEach((m) => m.dispose());
    host.meshes.delete(cardInstance.id);
    // Hover only refreshes on pointer move, so a hover pointing at this
    // mesh goes stale the instant it leaves the table. And the toHand /
    // toCemetery / flip / tap hotkeys act on hoveredMesh. Clear it so a
    // repeat key press without mouse movement is a no-op instead of
    // re-running the action on a card that's no longer on the board.
    if (host.hoveredMesh === mesh) host.hoveredMesh = null;
  }
  host.lifeHUDs.delete(cardInstance.id);
  host.statusHUDs.delete(cardInstance.id);
  host.counterHUDs.delete(cardInstance.id);
  host._removeAllCardEffects(cardInstance.id);
  host.selectedCardIds.delete(cardInstance.id);
  host.ownership.forget(cardInstance.id);
  // Drop any ledger entries that came from this card, recalculate
  // affinities, and rebuild static auras. Shared with sendCardToPile.
  host._disposeCardEffectsOnLeave(cardInstance, broadcast);
  if (broadcast) {
    host._broadcastRemoveCard(cardInstance.id);
    // A staged removal (cancelling a not-yet-committed play by
    // dragging it back into the hand) has nothing to undo. The
    // site mana bump and affinity recalc never happened in the
    // first place. Skip both so the trackers don't flicker.
    if (!staged && cardInstance.isSite) {
      const player = cardInstance.rotated ? 'p2' : 'p1';
      host.recalculateSiteAffinities(player);
      if (host._siteProvidesMana(cardInstance)) {
        // Site leaves play → -1 mana for the controller (floors at 0
        // inside payMana). Skipped silently when already at 0.
        host.playerResources.payMana(player, 1);
      }
    }
  }
  if (host.equipmentOverlayRefs.has(cardInstance.id)) {
    host.syncEquipmentOverlays();
  }
}

// Return a card to ITS OWNER's hand, not the local player's. Used by
// the silent `return_self_to_hand` op (Mariner's Curse, Evil Presence):
// the trigger fires on the client of whoever moved a minion into the
// aura, which may be the OPPONENT of the aura's owner. sendToHand /
// addToHand always route to the local hand in multiplayer, so a naive
// call would drop the aura into the mover's hand. Mirror the
// RETURN_TO_HAND_TARGET ownership gate: when the aura is local, pick it
// up here; otherwise despawn locally and hand the pickup off to the
// owner's client via `card:tohand`, which adds it to their hand.
export function returnCardToOwnerHand(host, cardInstance) {
  if (host.isCardOnLocalSide(cardInstance)) {
    host.sendToHand(cardInstance);
    return;
  }
  host.removeCardFromTable(cardInstance, false);
  // Auth: the server's return_self_to_hand owns the owner-hand pickup.
  if (host._authZonesEnabled?.()) return;
  host.sync.returnToHand(cardInstance.id);
}

export function sendToHand(host, cardInstance) {
  // A special spellcaster leaving the realm. By ANY exit, including a
  // bounce back to hand. Loses its owner-locked hand; the remaining spells
  // go to the owner's cemetery (codex). sendCardToPile covers the Cemetery /
  // Banished exits; return-to-hand routes through here, so flush here too.
  // No-op for a card that owns no unit hand.
  host._flushUnitHandOnLeave?.(cardInstance.id);
  // Snapshot face-down BEFORE removeCardFromTable, which calls
  // resetCardOnLeavePlay → wipes faceDown. Without this, the log line
  // would fall through to cardInstance.name and reveal the identity of
  // a card that the opponent never saw face-up.
  const wasFaceDown = !!cardInstance.faceDown;
  host.removeCardFromTable(cardInstance);
  host.addToHand(cardInstance);
  // Auth zones: record the board → hand move so the server's hands[owner] gets
  // the card (registry zone becomes 'hand'). Without this, removeCardFromTable
  // only broadcast a legacy P2P mesh removal. The card was orphaned in the
  // server registry (no hand entry, no mesh) and vanished, never returning on
  // reload. Returns to its OWNER's hand (Ownership rule; the reducer keys off
  // card.owner). Every other board-exit (sendCardToPile / draw) already emits.
  if (host._authZonesEnabled?.()) host._emitMoveToZone(cardInstance.id, 'hand');
  const label = wasFaceDown ? msg('a face-down card') : (cardInstance.name || msg('a card'));
  host.logAction('Picked up {card} to hand', { card: label });
  host.setState({ contextMenu: null });
}

// Cards are never deleted outright. They're banished, which sends
// them to the owner's Banished pile (UI-only, opened from the HUD
// portal button). sendCardToPile handles broadcast + animation.
export function banishCard(host, cardInstance) {
  host.sendCardToPile(cardInstance, 'Banished');
}

export function deleteToken(host, tokenInstance) {
  const mesh = host.tokenMeshes.get(tokenInstance.id);
  if (mesh) {
    host.scene.scene.remove(mesh);
    mesh.geometry.dispose();
    mesh.material.dispose();
    host.tokenMeshes.delete(tokenInstance.id);
  }
  const { gameState } = host.state;
  gameState.tokens = gameState.tokens.filter((t) => t.id !== tokenInstance.id);
  host.setState({ contextMenu: null });
}

// Snap point for the LOCAL player's cemetery. Driven by whether the
// local client is host (p1 → 'cemetery') or guest (p2 → 'cemetery2').
export function getLocalCemeterySnapPoint(host) {
  const localRotated = host.getActivePlayerRotated();
  const key = localRotated ? 'cemetery2' : 'cemetery';
  return getSpawnPoint(host.state.spawnConfig, key);
}

// Snap points for the local player's two decks, used by the
// drag-to-deck-top drop zone below.
export function getLocalDeckSnapPoints(host) {
  const localRotated = host.getActivePlayerRotated();
  return {
    Spellbook: getSpawnPoint(host.state.spawnConfig, localRotated ? 'spellbook2' : 'spellbook'),
    Atlas:     getSpawnPoint(host.state.spawnConfig, localRotated ? 'atlas2' : 'atlas'),
  };
}

// Return the local deck ('Spellbook' / 'Atlas') whose snap point is
// within drop radius of (x, z), or null if the point is over neither.
// Uses CEMETERY_DROP_RADIUS. The decks use the same visual footprint.
export function getLocalDeckDropTarget(host, x, z) {
  const snaps = host.getLocalDeckSnapPoints();
  for (const [name, snap] of Object.entries(snaps)) {
    if (!snap) continue;
    const dx = x - snap.x;
    const dz = z - snap.z;
    if (dx * dx + dz * dz <= CEMETERY_DROP_RADIUS_SQ) return name;
  }
  return null;
}

// True when world-space (x, z) is inside the local cemetery's drop zone.
export function isWithinLocalCemeteryDropZone(host, x, z) {
  const snap = host.getLocalCemeterySnapPoint();
  const dx = x - snap.x;
  const dz = z - snap.z;
  return dx * dx + dz * dz <= CEMETERY_DROP_RADIUS_SQ;
}

// Locate the local player's cemetery pile mesh (may be null if the pile
// is empty. Meshes are created lazily when the first card lands).
export function getLocalCemeteryPileMesh(host) {
  const localRotated = host.getActivePlayerRotated();
  const cemeteryId = localRotated ? 'cemetery-p2' : 'cemetery-p1';
  return host.pileMeshes.get(cemeteryId) || null;
}

// During a card drag, highlight the local cemetery pile mesh when the
// primary dragged card is locally owned and the cursor is inside the
// drop zone. Stored separately from this.hoveredMesh because normal
// hover detection is bypassed while dragging.
export function updateCemeteryDragHover(host, worldX, worldZ) {
  const primary = host.dragging?.members?.[0];
  const primaryCard = primary?.mesh?.userData?.cardInstance;
  const localRotated = host.getActivePlayerRotated();
  const eligible = primaryCard
    && !!primaryCard.rotated === localRotated
    && host.isWithinLocalCemeteryDropZone(worldX, worldZ);
  const target = eligible ? host.getLocalCemeteryPileMesh() : null;

  if (host.cemeteryDragHoverMesh === target) return;

  if (host.cemeteryDragHoverMesh?.material) {
    const mats = Array.isArray(host.cemeteryDragHoverMesh.material)
      ? host.cemeteryDragHoverMesh.material
      : [host.cemeteryDragHoverMesh.material];
    mats.forEach((m) => { if (m.emissive) m.emissive.setHex(0x000000); });
  }
  if (target?.material) {
    const mats = Array.isArray(target.material) ? target.material : [target.material];
    mats.forEach((m) => { if (m.emissive) m.emissive.setHex(0x4a1515); });
  }
  host.cemeteryDragHoverMesh = target;
}

export function clearCemeteryDragHover(host) {
  if (!host.cemeteryDragHoverMesh) return;
  if (host.cemeteryDragHoverMesh.material) {
    const mats = Array.isArray(host.cemeteryDragHoverMesh.material)
      ? host.cemeteryDragHoverMesh.material
      : [host.cemeteryDragHoverMesh.material];
    mats.forEach((m) => { if (m.emissive) m.emissive.setHex(0x000000); });
  }
  host.cemeteryDragHoverMesh = null;
}

// Locate the local player's Spellbook or Atlas pile mesh. Returns
// null when the pile is empty (meshes are created lazily by
// updatePileMeshes once the pile holds at least one card).
export function getLocalDeckPileMesh(host, deckName) {
  const localRotated = host.getActivePlayerRotated();
  const pileId = `${deckName.toLowerCase()}-${localRotated ? 'p2' : 'p1'}`;
  return host.pileMeshes.get(pileId) || null;
}

// During a card drag, highlight the local deck pile (Spellbook or
// Atlas) whose drop zone contains the primary dragged card. Mirrors
// updateCemeteryDragHover. Same emissive tint, same eligibility
// guard (card must be locally owned).
export function updateDeckDragHover(host, worldX, worldZ) {
  const primary = host.dragging?.members?.[0];
  const primaryCard = primary?.mesh?.userData?.cardInstance;
  const localRotated = host.getActivePlayerRotated();
  const ownedLocally = primaryCard && !!primaryCard.rotated === localRotated;
  const deckName = ownedLocally ? host.getLocalDeckDropTarget(worldX, worldZ) : null;
  const target = deckName ? host.getLocalDeckPileMesh(deckName) : null;

  if (host.deckDragHoverMesh === target) return;

  if (host.deckDragHoverMesh?.material) {
    const mats = Array.isArray(host.deckDragHoverMesh.material)
      ? host.deckDragHoverMesh.material
      : [host.deckDragHoverMesh.material];
    mats.forEach((m) => { if (m.emissive) m.emissive.setHex(0x000000); });
  }
  if (target?.material) {
    const mats = Array.isArray(target.material) ? target.material : [target.material];
    // Warm gold tint to distinguish deck-drop from the cemetery's red.
    mats.forEach((m) => { if (m.emissive) m.emissive.setHex(0x4a3510); });
  }
  host.deckDragHoverMesh = target;
}

export function clearDeckDragHover(host) {
  if (!host.deckDragHoverMesh) return;
  if (host.deckDragHoverMesh.material) {
    const mats = Array.isArray(host.deckDragHoverMesh.material)
      ? host.deckDragHoverMesh.material
      : [host.deckDragHoverMesh.material];
    mats.forEach((m) => { if (m.emissive) m.emissive.setHex(0x000000); });
  }
  host.deckDragHoverMesh = null;
}

export function findOrCreateCemetery(host, rotated) {
  const isP2 = !!rotated;
  const { gameState, spawnConfig } = host.state;

  const cemeteryId = isP2 ? 'cemetery-p2' : 'cemetery-p1';
  const cemeteryName = isP2 ? k('Cemetery P2') : k('Cemetery P1');
  const spawnKey = isP2 ? 'cemetery2' : 'cemetery';

  let pile = gameState.piles.find((p) => p.id === cemeteryId);
  if (pile) return pile;

  const point = getSpawnPoint(spawnConfig, spawnKey);
  pile = {
    id: cemeteryId,
    name: cemeteryName,
    cards: [],
    x: point.x,
    z: point.z,
    rotated: isP2,
  };
  gameState.piles.push(pile);
  // The mesh is created lazily by updatePileMeshes once the first card
  // lands. CreatePileMesh returns null for empty piles.
  return pile;
}

// Lazily materialize a Collection pile for the local player. Unlike
// Banished, Collection IS visible as a 3D pile when populated, but
// it doesn't auto-spawn with the deck unless the deck declared a
// collection zone. Players can still want to PUT cards into
// Collection (via the card context menu) before any cards land
// there. And per the Collection HUD button being always-visible,
// they may want to open the (currently empty) view too. This helper
// creates the empty pile so both flows work without a populated deck.
export function findOrCreateCollection(host, rotated = null) {
  const localRotated = rotated == null ? host.getActivePlayerRotated() : !!rotated;
  const existing = host.state.gameState.piles.find(
    (p) => p.name === 'Collection' && !!p.rotated === localRotated,
  );
  if (existing) return existing;

  // Pull the configured spawn point so the empty pile sits where the
  // player set it; falls back to the engine's default if unconfigured.
  const { spawnConfig } = host.state;
  const suffix = localRotated ? '2' : '';
  const point = getSpawnPoint(spawnConfig, `collection${suffix}`) || { x: -30, z: 30 };

  const pile = {
    id: localRotated ? 'collection-p2' : 'collection-p1',
    name: k('Collection'),
    cards: [],
    x: point.x,
    z: point.z,
    rotated: localRotated,
  };
  host.state.gameState.piles.push(pile);
  return pile;
}

// Banished is UI-only: searched via the HUD portal button or its
// keybinding, never rendered as a 3D pile (createPileMesh skips it).
// Position is irrelevant but we keep the shape consistent with other
// piles so syncBridge can transmit it without special casing.
export function findOrCreateBanished(host, rotated) {
  const isP2 = !!rotated;
  const { gameState } = host.state;

  const banishedId = isP2 ? 'banished-p2' : 'banished-p1';
  const banishedName = isP2 ? k('Banished P2') : k('Banished P1');

  let pile = gameState.piles.find((p) => p.id === banishedId);
  if (pile) return pile;

  pile = {
    id: banishedId,
    name: banishedName,
    cards: [],
    x: 0,
    z: 0,
    rotated: isP2,
  };
  gameState.piles.push(pile);
  return pile;
}

// Replace a site card with a Rubble token at the same position and
// send the original site to the acting player's own cemetery.
export function turnToRubble(host, cardInstance, opts = {}) {
  const mesh = host.meshes.get(cardInstance.id);
  if (!mesh) { host.closeContextMenu(); return; }

  const rubbleCard = host._getFullCard('sorcery-rubble');
  if (!rubbleCard) {
    console.warn('[turnToRubble] Rubble card not found in sorceryCards');
    host.closeContextMenu();
    return;
  }

  const cell = host._cellAtWorld(mesh.position.x, mesh.position.z);

  // Resolve the log label BEFORE removeCardFromTable. It calls
  // resetCardOnLeavePlay, which clears faceDown, and reading it after
  // would reveal the identity of a site destroyed while face down.
  const label = cardLogLabel(cardInstance, msg('a site'));
  // Send the original site to the card owner's pile and sync it. The
  // destination follows the triggering effect: destruction → Cemetery
  // (default), banishment → Banished. Routing every destroyed site to
  // the cemetery would silently swallow banish effects.
  const dest = opts.dest === 'Banished' ? k('Banished') : k('Cemetery');
  const destPile = dest === 'Banished'
    ? host.findOrCreateBanished(cardInstance.rotated)
    : host.findOrCreateCemetery(cardInstance.rotated);
  host.removeCardFromTable(cardInstance);
  destPile.cards.push(cardInstance);
  host.updatePileMeshes();
  // Authoritative move (board → cemetery/banished). emitPileSyncOrZone emits the
  // MOVE_TO_ZONE so matchState records it (reconnect-safe) AND keeps the public
  // pile:sync for the face-up discard render.
  emitPileSyncOrZone(host, destPile, dest, cardInstance);
  host.logAction('Sent {name} to {pile}', { name: label, pile: msg(dest) });

  // The Tower of Babel is one site "for all purposes": destroying it sends
  // BOTH The Apex of Babel and The Base of Babel to the cemetery, but leaves
  // a single Rubble. If the destroyed card is one half, route its partner
  // (the other Babel site sharing this cell, same controller) to the
  // cemetery too before the Rubble spawns below.
  const partnerName = cardInstance.name === 'The Apex of Babel' ? 'The Base of Babel'
    : cardInstance.name === 'The Base of Babel' ? 'The Apex of Babel' : null;
  if (partnerName && cell) {
    for (const [, m] of host.meshes) {
      const partner = m?.userData?.cardInstance;
      if (!partner?.isSite || partner.name !== partnerName) continue;
      if (!!partner.rotated !== !!cardInstance.rotated) continue;
      const pCell = host._cellAtWorld(m.position.x, m.position.z);
      if (!pCell || pCell.col !== cell.col || pCell.row !== cell.row) continue;
      const partnerLabel = cardLogLabel(partner, msg('a site'));
      const partnerPile = dest === 'Banished'
        ? host.findOrCreateBanished(partner.rotated)
        : host.findOrCreateCemetery(partner.rotated);
      host.removeCardFromTable(partner);
      partnerPile.cards.push(partner);
      emitPileSyncOrZone(host, partnerPile, dest, partner);
      host.logAction('Sent {name} to {pile}', { name: partnerLabel, pile: msg(dest) });
      break;
    }
    host.updatePileMeshes();
  }

  let spawned = null;
  if (cell) {
    spawned = host._spawnSiteAtCell(rubbleCard, cell, {
      rotated: !!cardInstance.rotated,
      fireSiteEntry: false,
    });
  }
  // The Great Drowning of Men: the resulting Rubble enters play
  // already Flooded. Setting the status here (not on every Rubble
  // spawn) keeps the codex default Rubble-is-land-site intact while
  // still letting this specific card path opt in. The status add
  // goes through the same applyStatusToggle + broadcast path used by
  // every other flood toggle so both clients stay in sync.
  if (opts.flooded && spawned) {
    const changed = applyStatusToggle(spawned, 'flooded', true);
    if (changed) {
      host.rebuildStatusBadges(spawned.id);
      host._broadcastCardStatus(spawned.id, 'flooded', true);
    }
  }
  host.closeContextMenu();
}

export function findPileByName(host, name) {
  return host.state.gameState.piles.find((p) => p.name === name);
}

// Find a pile by name owned by the LOCAL player. Used by the C/Z/X
// hotkeys so the host's Spellbook isn't returned for the guest.
export function findLocalPileByName(host, name) {
  const localRotated = host.getActivePlayerRotated();
  return host.state.gameState.piles.find(
    (p) => p.name === name && !!p.rotated === localRotated,
  );
}

// Variant of findLocalPileByName that targets an arbitrary player
// by number. Used by casual sandbox where the local user drives
// both P1 and P2 decks.
export function findPileByNameForPlayer(host, name, playerNum) {
  const rotated = playerNum === 2;
  return host.state.gameState.piles.find(
    (p) => p.name === name && !!p.rotated === rotated,
  );
}

// Resolve the destination pile for a context-menu "put into pile"
// action.
//
// In multiplayer both players have identically-named piles (two
// "Spellbook"s, two "Atlas"s, etc.) distinguished only by the
// `rotated` flag that marks which player owns them.
//
// Non-Cemetery piles route to the LOCAL PLAYER's pile. In a sandbox
// tabletop "Spellbook (bottom)" means "my Spellbook", regardless of
// which player originally owned the card. If you need to put a card
// in the opponent's pile, drag it there manually.
//
// Cemetery is the exception: it routes to the CARD OWNER's cemetery,
// matching the TCG convention that destroyed cards go back to their
// owner's graveyard.
export function findDestinationPile(host, cardInstance, pileName) {
  // Owner-anchored unit-hand spell ("Only It Can Cast"): a card played
  // from a unit hand always returns to its OWNER's piles, not the current
  // controller's. `_ownerSeat` ('p1'|'p2') is the draw-time owner seat and
  // overrides the controller-follow `rotated` flag for resting-place
  // routing (Morgana FAQ). 'p2' maps to the rotated (away) side.
  const ownerSeat = cardInstance?._ownerSeat;
  if (ownerSeat && (pileName === 'Cemetery' || pileName === 'Banished')) {
    const ownerRotated = ownerSeat === 'p2';
    return pileName === 'Cemetery'
      ? host.findOrCreateCemetery(ownerRotated)
      : host.findOrCreateBanished(ownerRotated);
  }
  if (pileName === 'Cemetery') {
    return host.findOrCreateCemetery(cardInstance.rotated);
  }
  if (pileName === 'Banished') {
    // Banished follows the same owner-routes-home convention as
    // Cemetery: a banished card always lands in its own owner's
    // banished pile, never the opponent's.
    return host.findOrCreateBanished(cardInstance.rotated);
  }
  const localRotated = host.getActivePlayerRotated();
  return host.state.gameState.piles.find(
    (p) => p.name === pileName && !!p.rotated === localRotated,
  );
}

// ── Staggered pile departures ────────────────────────────────────────
// Multi-death events (combat sweeps, board wipes) commit their pile
// state synchronously but stage the VISUAL departure one card at a
// time. And only after any combat scene has left the stage. So the
// player can read who is leaving the battlefield. The removal
// broadcast rides inside each departure, so the opponent's client
// receives (and renders) the same procession.
const DEPART_STAGGER_MS = 450;

export function queuePileDeparture(host, fly) {
  if (!host._pileDepartureQueue) host._pileDepartureQueue = [];
  host._pileDepartureQueue.push(fly);
  drainPileDepartures(host);
}

// Is a fight currently owning the stage? The SYNCHRONOUS linger-timer
// field is checked first. It's a plain instance field, true the moment
// it happens. While the state checks are the client's view of an
// in-flight fight.
function combatStageBusy(host) {
  if (host._combatClearTimer) return true;        // resolved record lingering → playback running or imminent
  if (host.state.combatScene) return true;        // scene on stage (both sides)
  const pc = host.state.gameState?.pendingCombat;
  if (pc && pc.phase !== 'resolved') return true; // in-flight record (guest mid-fight)
  return false;
}

export function drainPileDepartures(host) {
  if (host._pileDepartureTimer || host._unmounted) return;
  const q = host._pileDepartureQueue;
  if (!q || !q.length) return;
  // Hold while a fight is being staged. Casualties leave after the
  // combat overlay has finished. Re-triggered from _closeCombatScene
  // and _clearPendingCombat.
  if (combatStageBusy(host)) return;
  const step = () => {
    host._pileDepartureTimer = null;
    if (host._unmounted) return;
    if (combatStageBusy(host)) return; // re-gate; resumes on scene close / record clear
    const fly = host._pileDepartureQueue.shift();
    if (fly) {
      try { fly(); } catch (e) { console.error('[pile] departure failed', e); }
    }
    if (host._pileDepartureQueue.length) {
      host._pileDepartureTimer = setTimeout(step, DEPART_STAGGER_MS);
    }
  };
  step();
}

export function sendCardToPile(host, cardInstance, pileName, shouldShuffle = false, position = 'top', opts = {}) {
  // Tokens (Skeletons, Frogs, Rubble, etc.) are banished whenever they
  // would leave the realm. They never enter the cemetery, hand, or a
  // deck. This is a blanket rule, so redirect every non-Banished pile
  // destination to Banished. Keeps cemetery-counting cards (Pillar of
  // Zeiros) from inflating on token deaths.
  if (cardInstance?.isToken && pileName !== 'Banished') {
    pileName = 'Banished';
  }
  const pile = host.findDestinationPile(cardInstance, pileName);
  if (!pile) {
    // Surface the cause of "the card just disappeared" reports.
    // a missing destination pile means findDestinationPile fell
    // through (typically a non-Cemetery pileName that doesn't
    // match any existing pile.name + rotated combination).
    console.warn('[sendCardToPile] no destination pile resolved', {
      cardId: cardInstance?.id,
      cardName: cardInstance?.name,
      cardRotated: cardInstance?.rotated,
      pileName,
    });
    host.setState({ contextMenu: null });
    return;
  }

  // Dedup guard: a rapid double-fire of the context-menu item used to
  // race the fly-to-pile animation. The second call would enter the
  // else-branch (because the first already removed the mesh from
  // host.meshes) and push the card into the pile; the first call's
  // animation callback would then fire and push the same instance a
  // second time, leaving Banished/Cemetery holding two entries with
  // the same id. Detect that here and bail.
  if (pile.cards.some((c) => c.id === cardInstance.id)) {
    console.warn('[sendCardToPile] dedup hit. Card already in pile', {
      cardId: cardInstance?.id,
      cardName: cardInstance?.name,
      pileId: pile.id,
    });
    host.setState({ contextMenu: null });
    return;
  }

  // v1.62. Site → Rubble transition. Per codex: "When a Site is
  // killed, it is replaced by a Rubble site." Sending a real site to
  // the cemetery or banished pile is destruction, so it auto-spawns a
  // Rubble token at the same cell to keep the location a site for
  // adjacency / placement purposes. Returning a site to the Atlas (its
  // own site deck) is NOT destruction. It goes back intact, no Rubble.
  // The existing turnToRubble handler owns the spawn. We just route
  // the destroyed site there before pile insertion. Rubble itself
  // skips this (avoid infinite recursion).
  // opts.noRubble skips the replacement: Kairos' site eviction is a
  // displacement (the returning archived site takes the cell), not
  // destruction, so no Rubble may spawn there.
  // opts.fromMirror skips it too: a mirror fold is render-only. The
  // Rubble was already minted upstream (actor's turnToRubble or the
  // server's automation mint), and re-entering here minted one extra
  // Rubble per client per diff (the stacked-triple-Rubble bug).
  if (cardInstance.isSite && cardInstance.cardId !== 'sorcery-rubble'
      && !opts.noRubble && !opts.fromMirror
      && (pileName === 'Cemetery' || pileName === 'Banished')) {
    host.turnToRubble(cardInstance, { ...opts, dest: pileName });
    host.setState({ contextMenu: null });
    return;
  }

  // fromMirror: a server-driven exit renders on BOTH clients, so anything
  // here that acts (broadcasts, emits intents, fires triggers) would run
  // once per client per diff. The Rubble-bug class. Log locally only, and
  // skip the trigger fires: the server's deathTriggers/deathSweep fan-out
  // owns on_death for deaths it realized, and the actor's non-mirror call
  // owns them for manual sends.
  if (opts.fromMirror) host.addLogEntry(msg('Sent {name} to {pile}', { name: cardLogLabel(cardInstance), pile: msg(pileName) }), 'system');

  else host.logAction('Sent {name} to {pile}', { name: cardLogLabel(cardInstance), pile: msg(pileName) });

  // Owner of a unit hand leaving the realm flushes its remaining
  // "Only It Can Cast" spells to the controller's cemetery. On a mirror
  // fold the flush must run on exactly ONE client (the hand's controller)
  // The server reducer already dropped the unitHands entry; this routes
  // the spells to the cemetery once instead of once per client.
  host._flushUnitHandOnLeave?.(cardInstance.id, { mirrorSingleWriter: !!opts.fromMirror });

  if (!opts.fromMirror) {
    // v1.15e: fire Deathrite (on_death) automation BEFORE the card
    // mesh is removed. Scope resolution still needs the source's
    // position. Only fires for Cemetery / Banished destinations.
    host._fireDeathriteOnLeave(cardInstance, pileName);
    // v1.42. Fire ON_REALM_EXIT watchers (other cards reacting to
    // this card leaving the realm). Distinct from Deathrite, which
    // fires on the leaving card itself. Watchers' source position
    // can still anchor scope since the mesh hasn't been removed yet.
    host._fireRealmTrigger(TRIGGER.ON_REALM_EXIT, cardInstance);
  }

  const cardMesh = host.meshes.get(cardInstance.id);

  // Detach equipment back onto the field before the host card leaves
  // it. The animated branch below skips removeCardFromTable, so
  // without this call the equippedCards array would ride along into
  // the pile. And pile:sync replicates that stale data to the
  // opponent. On replay from the pile the equipment would silently
  // re-attach on both clients.
  if (cardMesh && cardInstance.equippedCards?.length) {
    const anchor = { x: cardMesh.position.x, z: cardMesh.position.z };
    host._detachAllEquipmentToField(cardInstance, anchor, { broadcast: !opts.fromMirror });
  }

  // Commit the move into the pile SYNCHRONOUSLY at entry. The previous
  // implementation deferred this push into the fly-to-pile animation
  // callback (~300ms later), opening the same race we just guarded
  // against. With the push up front, the dedup check above will catch
  // any concurrent re-entry, and the animation becomes purely visual.
  if (position === 'bottom') {
    pile.cards.unshift(cardInstance);
  } else {
    pile.cards.push(cardInstance);
  }

  // The pile MESH is created lazily on first landing (createPileMesh
  // returns null for empty piles). A freshly created cemetery has
  // none yet. The pile object always carries its table coordinates,
  // so the flight can target those: without this fallback the first
  // casualty of a match skipped the animated branch entirely and
  // vanished instantly (bypassing the staggered-departure queue).
  const pileMesh = host.pileMeshes.get(pile.id);
  const pileX = pileMesh ? pileMesh.position.x : pile.x;
  const pileZ = pileMesh ? pileMesh.position.z : pile.z;

  if (cardMesh && pileX != null && pileZ != null) {
    host.meshes.delete(cardInstance.id);
    // The mesh leaves host.meshes now (so it's non-interactable during the
    // staggered flight) but stays TRACKED as an exiting mesh. The board
    // reconciler force-disposes it if the flight is ever stranded (combat
    // gate never clears), so it can never become an orphan ghost.
    trackExitingMesh(host, cardInstance.id, cardMesh);
    host.lifeHUDs.delete(cardInstance.id);
    host.statusHUDs.delete(cardInstance.id);
    host.counterHUDs.delete(cardInstance.id);
    host._removeAllCardEffects(cardInstance.id);
    // v1.15b: drop ledger entries from the leaving card and rebuild
    // affinities + static auras. Without this, a Castle Servants
    // banished via the context menu would leave its (A) threshold
    // permanently on the controller's tracker.
    host._disposeCardEffectsOnLeave(cardInstance, true);
    // Recalculate affinities and drop mana now (after meshes.delete)
    // so the counters update immediately, even before the fly-to-pile
    // animation finishes. Rubble and dead sites are excluded from
    // the mana adjustment.
    if (cardInstance.isSite) {
      const player = cardInstance.rotated ? 'p2' : 'p1';
      host.recalculateSiteAffinities(player);
      if (host._siteProvidesMana(cardInstance)) {
        // Site leaves play → -1 mana for the controller (floors at 0).
        host.playerResources.payMana(player, 1);
      }
    }
    const depart = () => animateCardToPile(cardMesh, pileX, pileZ, host.scene.scene, () => {
      if (shouldShuffle) {
        shufflePile(host.state.gameState, pile.id);
        const pm = host.pileMeshes.get(pile.id);
        if (pm) animateShufflePile(pm, pile, host.scene.scene);
      }
      host.updatePileMeshes();
      // fromMirror: the server already moved this card (auth-driven death/exit),
      // so re-broadcasting would loop. Animate + place locally only.
      if (!opts.fromMirror) {
        host._broadcastRemoveCard(cardInstance.id, pile.id);
        emitPileSyncOrZone(host, pile, pileName, cardInstance, position);
        // "Shuffle in" is two server mutations, not one (#53): without the
        // SHUFFLE the card was merely appended and the deck kept its order.
        // AFTER the move emit. Same socket, so the server shuffles a pile
        // that already contains the card.
        if (shouldShuffle) {
          logShuffle(host, pileName);
          host._emitShuffle?.(pile);
        }
      }
      // Flight done (animateCardToPile already removed + disposed the
      // mesh). Clear the exit tracking so the reconciler stops watching it.
      untrackExitingMesh(host, cardInstance.id);
    });
    // queueDeparture: the pile state above is already committed; only the
    // flight (and the broadcasts riding in its callback) staggers. The
    // departing mesh is out of host.meshes (non-interactable) but stays in
    // host._exitingMeshes, so if this flight is ever stranded behind the
    // combat gate the board reconciler force-disposes it. It can never
    // become a permanent orphan ghost.
    if (opts.queueDeparture) queuePileDeparture(host, depart);
    else depart();
  } else {
    if (shouldShuffle) {
      shufflePile(host.state.gameState, pile.id);
      const pm = host.pileMeshes.get(pile.id);
      if (pm) animateShufflePile(pm, pile, host.scene.scene);
    }
    host.removeCardFromTable(cardInstance, !opts.fromMirror);
    host.updatePileMeshes();
    if (!opts.fromMirror) {
      emitPileSyncOrZone(host, pile, pileName, cardInstance, position);
      if (shouldShuffle) {
        logShuffle(host, pileName);
        host._emitShuffle?.(pile);
      }
    }
  }

  // Sanity check: the card should be in pile.cards now. If it
  // isn't, the dedup guard fired AFTER our push (impossible) or
  // some other path mutated pile.cards out from under us. Logging
  // surfaces the gap; the legitimate path always satisfies host.
  if (!pile.cards.some((c) => c.id === cardInstance.id)) {
    console.warn('[sendCardToPile] card missing from pile after push', {
      cardId: cardInstance?.id,
      cardName: cardInstance?.name,
      pileId: pile.id,
      pileCardCount: pile.cards.length,
    });
  }

  host.setState({ contextMenu: null });
}

export function sendHandCardToPile(host, cardInstance, pileName, shouldShuffle = false, position = 'top') {
  // Same blanket token rule as sendCardToPile: a token leaving hand is
  // banished, never sent to the cemetery or a deck.
  if (cardInstance?.isToken && pileName !== 'Banished') {
    pileName = 'Banished';
  }
  const pile = host.findDestinationPile(cardInstance, pileName);
  // Dedup guard: refuse to push if the card is somehow already in the
  // destination pile. Same intent as sendCardToPile. A racy double-
  // fire of the hand context menu shouldn't duplicate the card.
  if (pile && !pile.cards.some((c) => c.id === cardInstance.id)) {
    if (position === 'bottom') { pile.cards.unshift(cardInstance); } else { pile.cards.push(cardInstance); }
    if (shouldShuffle) {
      shufflePile(host.state.gameState, pile.id);
      const mesh = host.pileMeshes.get(pile.id);
      if (mesh) animateShufflePile(mesh, pile, host.scene.scene);
    }
    host.updatePileMeshes();
    emitPileSyncOrZone(host, pile, pileName, cardInstance, position);
    // See sendCardToPile: the shuffle is its own server mutation, emitted
    // after the move so the server shuffles a pile that already holds the card.
    if (shouldShuffle) {
      logShuffle(host, pileName);
      host._emitShuffle?.(pile);
    }
  }
  host.removeFromHand(cardInstance);
  host.setState({ contextMenu: null, hoveredHandIndex: -1 });
}

// Reveal is an authoritative SET_REVEALED mutation: the server unions the
// opponent seat into cards[id].revealedTo and the per-seat projection stops
// skeletonizing the card, so the opponent's hand fan renders it face-up.
// revealedTo is sticky server-side, matching the sticky nameKnown below.
// un-revealing only clears the local eye badge.
function emitHandReveal(host, cardInstance) {
  if (!host._authZonesEnabled?.()) return; // sandbox: local state only, no wire
  const opp = host._localSeat() === 'p1' ? 'p2' : 'p1';
  sendMatchCommand(host._matchKey(), revealCommand(cardInstance.id, [opp]));
}

// Toggle whether a hand card is revealed to the opponent. The card
// instance is mutated in place (consistent with flipCard, tapCard,
// etc.) and the reveal is pushed through SET_REVEALED so the opponent
// swaps the back mesh for a face mesh in that slot. Local UI shows an
// eye icon on any card with `revealed === true`.
export function toggleHandCardReveal(host, cardInstance) {
  cardInstance.revealed = !cardInstance.revealed;
  // Showing a hand card to the opponent makes its identity public; the
  // flag is sticky, so un-revealing it later doesn't un-see it.
  if (cardInstance.revealed) {
    cardInstance.nameKnown = true;
    emitHandReveal(host, cardInstance);
  }
  if (cardInstance.revealed) host.logAction('Revealed {card} to opponent', { card: cardLogLabel(cardInstance) });
  else host.logAction('Stopped revealing {card}', { card: cardLogLabel(cardInstance) });
  host.setState({ contextMenu: null, hoveredHandIndex: -1 });
  host.forceUpdate();
}

// Bulk reveal: mark every card currently in the hand as shown to the
// opponent. One-way. There's no "stop showing entire hand" action.
// Cards drawn after this still enter face-down (per addToHand) and
// need their own per-card reveal, or another bulk reveal click. The
// hand the action targets is inferred from the right-clicked card so
// it works correctly in casual sandbox where two hands coexist.
export function revealEntireHand(host, cardInstance) {
  const handKey = host._handStateKeyForCard(cardInstance);
  const hand = host.state[handKey] || [];
  let revealedCount = 0;
  for (const card of hand) {
    if (!card.revealed) {
      card.revealed = true;
      card.nameKnown = true;
      emitHandReveal(host, card);
      revealedCount++;
    }
  }
  if (revealedCount > 0) {
    host.logAction('Revealed entire hand to opponent');
  }
  host.setState({ contextMenu: null, hoveredHandIndex: -1 });
  host.forceUpdate();
}

// Flip a hand card's faceDown state. Purely local: opponents never see
// individual hand cards, so there's no sync. The flag travels with the
// card when it's later dragged onto the table, where `placeCard`
// sanitizes the network payload for face-down placements.
export function toggleHandCardFaceDown(host, cardInstance) {
  // Immutable state update. The in-place mutation + forceUpdate
  // pattern was flaky with the dual-hand renderer. Replacing the
  // card in the hand array forces React's diff to pick up the
  // flag change and re-render the image src.
  const handKey = host._handStateKeyForCard(cardInstance);
  host.setState((state) => ({
    contextMenu: null,
    [handKey]: (state[handKey] || []).map((c) =>
      c.id === cardInstance.id ? { ...c, faceDown: !c.faceDown } : c,
    ),
  }));
  playSound('cardFlip');
}

// Only the hidden order-bearing decks have a shuffle the server records
// (the same TOP_INDEXED_ZONES gate _emitShuffle uses); a cemetery/banished
// "shuffle" is a local no-op the mirror reverts, so announcing it would put a
// shuffle in the shared log that the game never had. Deck names carry no seat
// suffix, so the logged name matches the send path's "Sent … to Spellbook".
export function logShuffle(host, pileName) {
  if (isTopIndexedZone(host._authZoneForPileName?.(pileName))) host.logAction('Shuffled {pile}', { pile: msg(pileName) });
}

export function shufflePileAction(host, pile) {
  const mesh = host.pileMeshes.get(pile.id);
  if (mesh) {
    animateShufflePile(mesh, pile, host.scene.scene);
  }
  playSound(pile.name === 'Atlas' ? 'cardShuffleAtlas' : 'cardShuffleSpellbook');
  shufflePile(host.state.gameState, pile.id);
  // The shuffle is visible to nobody but the shuffler (a flourish on the pile
  // mesh and a sound), so without this line the opponent has no record that a
  // "shuffle your spellbook" clause was honored (valkenhall-qa#145).
  logShuffle(host, pile.name);
  host.setState({ contextMenu: null });
  if (host._authZonesEnabled?.()) {
    // Under auth the local shuffle above is a PLACEHOLDER, not the result: the
    // server discards the asserted order and commits its own crypto permutation
    // (P7 anti-cheat), which adoptOwnPileOrderFromMirror folds into the render
    // one RTT later. The emit's order payload is therefore dead on arrival.
    // it stays only because the command shape is shared with the reducer.
    host._emitShuffle(pile);
  } else {
  }
}

// Toggle public visibility of the local Collection pile. When `true`,
// the opponent's PileSearchDialog gets a read-only tab for this pile;
// pile contents already mirror live via pile:sync, so the tab updates
// in real time as cards move in and out.
export function toggleCollectionRevealed(host) {
  // Create-or-find: the Collection pile is materialized lazily, so the
  // visibility toggle must be able to spin one up rather than no-op
  // when the player flips it before ever opening their Collection.
  const pile = host.findLocalPileByName('Collection') || host.findOrCreateCollection();
  pile.revealed = !pile.revealed;
  if (host._authZonesEnabled?.()) {
    // Server-authoritative share (R2d): the reveal flag + collection contents
    // are server-owned, so the opponent renders our collection from the
    // projected mirror and the share survives reconnect. No P2P pile:sync.
    host._emitPileRevealed('collection', pile.revealed);
  } else {
    // Legacy / sandbox: broadcast the full pile (byte-unchanged).
  }
  if (pile.revealed) host.logAction('Revealed Collection to opponent');
  else host.logAction('Hid Collection from opponent');
  host.forceUpdate();
}

/**
 * Spawn a fresh copy of an on-table card next to the original. The
 * clone carries only the card's identity (name, image, type) and
 * controller (`rotated`). Tapped state, face direction, damage,
 * statuses, and equipment are all reset. Matches a tabletop-sim
 * "make a token copy" semantic, not a state-preserving mirror.
 */
export function duplicateCard(host, cardInstance) {
  if (!cardInstance) return;
  // Auth: the server mints the copy (manualService, deterministic id) so it is
  // real MatchState. A client-minted clone never entered the mirror and
  // vanished on the next Full. Both seats render it via rebuildBoardFromMirror
  // within one round-trip; no optimistic local mesh.
  if (host._authZonesEnabled?.()) {
    host._sendMatchIntent(duplicateIntent(cardInstance.id));
    host.logAction('Duplicated {card}', { card: cardLogLabel(cardInstance) });
    return;
  }
  const mesh = host.meshes.get(cardInstance.id);
  const baseX = mesh ? mesh.position.x : (cardInstance.x ?? 0);
  const baseZ = mesh ? mesh.position.z : (cardInstance.z ?? 0);
  const clone = {
    id: `${cardInstance.id}-dup-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    cardId: cardInstance.cardId,
    name: cardInstance.name,
    imageUrl: cardInstance.imageUrl,
    imagePath: cardInstance.imagePath,
    foiling: cardInstance.foiling,
    type: cardInstance.type,
    isSite: cardInstance.isSite,
    isToken: cardInstance.isToken,
    tapped: false,
    faceDown: false,
    rotated: cardInstance.rotated,
    x: baseX + 3,
    y: 0,
    z: baseZ + 3,
    ...(cardInstance.backFaceImagePath || cardInstance.backFaceImageUrl ? {
      face: 'front',
      backFaceImageUrl: cardInstance.backFaceImageUrl,
      backFaceImagePath: cardInstance.backFaceImagePath,
    } : {}),
  };
  host.addCardToTable(clone, false, { silent: true });
  host.logAction('Duplicated {card}', { card: cardLogLabel(cardInstance) });
}

export function updatePileMeshes(host) {
  const { gameState } = host.state;
  for (const pile of gameState.piles) {
    const mesh = host.pileMeshes.get(pile.id);

    if (!mesh) {
      // Lazy-create the mesh the first time a pile becomes non-empty.
      // Cemeteries are created with an empty card list, so createPileMesh
      // returns null at creation time. We build it here once a card lands.
      if (pile.cards.length > 0) {
        const newMesh = createPileMesh(pile);
        if (newMesh) {
          host.scene.scene.add(newMesh);
          host.pileMeshes.set(pile.id, newMesh);
        }
      }
      continue;
    }

    if (pile.cards.length === 0) {
      host.scene.scene.remove(mesh);
      mesh.geometry.dispose();
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      mats.forEach((m) => m.dispose());
      host.pileMeshes.delete(pile.id);
    } else {
      updatePileMesh(mesh, pile);
    }
  }
}

// `opts.silent` suppresses the log line for callers that already write their
// own, more specific summary (a named tutor effect, a multi-pick window).
export function takeCardFromPile(host, pile, cardInstance, opts = {}) {
  // Engine mode: only the pile owner may pull cards out. Opponent
  // still gets the search dialog to READ the pile (that's how we
  if (pile.infinite) {
    // Token pile. Clone with a fresh id so the same token can be
    // spawned unlimited times without depleting the virtual pile.
    // Set rotated based on the local player so ownership is correct.
    // In casual sandbox, the token follows whichever side is active.
    const isLocalP2 = host.props.isCasualSandbox
      ? host.state.activePlayerNum === 2
      : !host.state.isHost;
    const clone = { ...cardInstance, id: `${cardInstance.id}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, rotated: isLocalP2 };
    host.addToHand(clone);
    return;
  }
  // Match by id, not by object identity: a stale cardInstance reference
  // (e.g. a click handler closed over the pre-pile:sync object) would
  // miss with indexOf even though the same card is in the pile. Bail
  // out entirely if the card isn't there. Without this, addToHand
  // would still fire on a stale reference, putting a phantom copy in
  // the hand while the original stays in the pile.
  const idx = pile.cards.findIndex((c) => c.id === cardInstance.id);
  if (idx === -1) return;
  const removed = pile.cards[idx];
  pile.cards.splice(idx, 1);

  // Authoritative ownership fix for casual sandbox: the PILE's
  // rotated flag is the ground truth (a card inside cemetery-p1
  // belongs to P1, full stop). Sync the card's rotated to match
  // before handing off to addToHand. This resolves a bug where
  // the card's own rotated flag had drifted via some earlier
  // interaction and the hand-routing put the card into the wrong
  // player's hand. Multiplayer behaviour is unchanged (pile and
  // card rotation always agree for local pulls there).
  if (host.props.isCasualSandbox) {
    removed.rotated = !!pile.rotated;
  }

  host.addToHand(removed);
  // A card leaving a hidden pile is invisible across the table: the pile only
  // shrinks by one, and the search dialog is local. Without this line a player
  // could pull as many named cards out of their own spellbook as they liked
  // and leave no record of it (valkenhall-qa#174). cardLogLabel keeps the
  // identity concealed until the card has actually been revealed, so the
  // opponent learns THAT a card was taken, never which one.
  if (!opts.silent) {
    host.logAction('Took {card} from {pile}', { card: cardLogLabel(removed), pile: msg(pile.name) });
  }
  if (host._authZonesEnabled?.()) {
    // Authoritative move (pile → hand): tutor/search pull. matchState records
    // the move so the hand + pile counts are reconnect-correct.
    host._emitMoveToZone(removed.id, 'hand');
  } else {
  }
  host.setState((state) => ({
    searchPile: pile.cards.length === 0 ? null : state.searchPile,
  }));
  host.updatePileMeshes();
}

// Pop the top card off a pile into the player's hand.
export function drawPileToHand(host, pile) {
  if (pile.cards.length > 0) {
    const card = pile.cards[pile.cards.length - 1];
    pile.cards.pop();
    host.addToHand(card);
    host.updatePileMeshes();
    if (host._authZonesEnabled?.()) {
      // Server-authoritative draw (the hand chokepoint): move the card into our
      // hand in the authoritative matchState so the hand is reconstructible from
      // the per-seat mirror on reconnect. The server projects the pile-shrink to
      // the opponent as a count delta. No pile:sync of our hidden draw pile.
      host._emitMoveToZone(card.id, 'hand');
    } else {
    }
    host.logAction('Drew a card to hand from {pile}', { pile: msg(pile.name || pile.id) });
  }
  host.setState({ contextMenu: null });
}
